import { apiGet, apiPost, apiPatch, apiDelete } from "../../shared/http.js";
import {
  CreateInviteResponseSchema,
  InviteListItemSchema,
  PatchHouseholdRequestSchema,
  HouseholdSchema,
  type CreateInviteResponse,
  type Household,
  type PatchHouseholdRequest,
} from "@recipevault/shared";
import { z } from "zod";

const MemberSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  email: z.string().email(),
  role: z.enum(["owner", "member"]),
  joinedAt: z.string(),
});
export type Member = z.infer<typeof MemberSchema>;

const MembersResponseSchema = z.object({
  members: z.array(MemberSchema),
  pendingInvites: z.array(InviteListItemSchema),
});
export type MembersResponse = z.infer<typeof MembersResponseSchema>;

export async function getMembers(hhId: string): Promise<MembersResponse> {
  const raw = await apiGet<unknown>(`/households/${hhId}/members`);
  return MembersResponseSchema.parse(raw);
}

export async function createInvite(
  hhId: string,
  email: string,
  idempotencyKey: string,
): Promise<CreateInviteResponse> {
  const raw = await apiPost<unknown>(
    `/households/${hhId}/invites`,
    { email },
    { idempotencyKey },
  );
  return CreateInviteResponseSchema.parse(raw);
}

export async function revokeInvite(hhId: string, inviteId: string): Promise<void> {
  await apiDelete(`/households/${hhId}/invites/${inviteId}`);
}

export async function removeMember(hhId: string, userId: string): Promise<void> {
  await apiDelete(`/households/${hhId}/members/${userId}`);
}

export async function patchHousehold(hhId: string, body: PatchHouseholdRequest): Promise<Household> {
  PatchHouseholdRequestSchema.parse(body);
  const raw = await apiPatch<unknown>(`/households/${hhId}`, body);
  const parsed = z.object({ household: HouseholdSchema }).parse(raw);
  return parsed.household;
}
